/**
 * ============================================================================
 * OBJECTS STORE - CACHE RETRIEVAL BY FOLDER
 * ============================================================================
 * 
 * Functions for retrieving cached objects filtered by folder hierarchy.
 * 
 * Features:
 * - Filters by folder ID
 * - Includes items in subfolders recursively
 * - Supports root-level filtering (no folder)
 * 
 * @module objects/cache/byFolder
 */

import { getAllSubfolderIds } from "../../folderStore";
import { objectsStore } from "../store";
import type { ObjectItem } from "../types";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if an item is in the root (no folder assigned).
 */
function isInRoot(folder: string | null | undefined): boolean {
  return !folder || folder === null || folder === '';
}

/**
 * Check if an item's folder is in a given set of folder IDs.
 */
function isInFolderSet(itemFolder: string | null | undefined, folderIds: string[]): boolean {
  return !!itemFolder && folderIds.includes(itemFolder);
}

// ============================================================================
// CACHE RETRIEVAL BY FOLDER
// ============================================================================

/**
 * Get cached library objects for a specific game and folder (including subfolders).
 * 
 * Behavior:
 * - If folderId is null/undefined/empty: Returns items in root (no folder)
 * - If folderId is provided: Returns items in that folder AND all subfolders
 * 
 * @param gameId - The game ID to filter by
 * @param folderId - The folder ID to filter by (null/undefined for root items)
 * @returns Array of cached objects in the specified folder hierarchy
 * 
 * @example
 * ```ts
 * // Get root-level objects
 * const rootObjects = getCachedObjectsByFolder(gameId);
 * 
 * // Get objects in a specific folder (including subfolders)
 * const folderObjects = getCachedObjectsByFolder(gameId, folderId);
 * ```
 */
export function getCachedObjectsByFolder(gameId: string, folderId?: string | null): ObjectItem[] {
  const allItems = objectsStore.getData();
  
  return allItems.filter(item => {
    // Filter by game and type
    if (item.game_id !== gameId) return false;
    if (item.type !== 'glossary' && item.type !== 'Library Object') return false;
    
    // Handle folder filtering
    if (isInRoot(folderId)) {
      // Show items in root (no folder)
      return isInRoot(item.folder);
    } else {
      // Show items in specific folder and all its subfolders
      const allFolderIds = getAllSubfolderIds(gameId, 'library', folderId!);
      return isInFolderSet(item.folder, allFolderIds);
    }
  });
}

/**
 * Get cached document items for a specific game and folder (including subfolders).
 * 
 * @param gameId - The game ID to filter by
 * @param folderId - The folder ID to filter by (null/undefined for root items)
 * @returns Array of cached document items in the specified folder hierarchy
 * 
 * @example
 * ```ts
 * const documents = getCachedDocumentItemsByFolder(gameId, folderId);
 * ```
 */
export function getCachedDocumentItemsByFolder(gameId: string, folderId?: string | null): ObjectItem[] {
  const allItems = objectsStore.getData();
  
  return allItems.filter(item => {
    // Filter by game and type
    if (item.game_id !== gameId) return false;
    if (item.type !== 'document') return false;
    
    // Handle folder filtering
    if (isInRoot(folderId)) {
      return isInRoot(item.folder);
    } else {
      const allFolderIds = getAllSubfolderIds(gameId, 'documents', folderId!);
      return isInFolderSet(item.folder, allFolderIds);
    }
  });
}

/**
 * Get cached library items (glossary + documents) for a specific game and folder.
 * 
 * @param gameId - The game ID to filter by
 * @param folderId - The folder ID to filter by (null/undefined for root items)
 * @returns Array of cached library items in the specified folder hierarchy
 * 
 * @example
 * ```ts
 * const allItems = getCachedLibraryItemsByFolder(gameId, folderId);
 * ```
 */
export function getCachedLibraryItemsByFolder(gameId: string, folderId?: string | null): ObjectItem[] {
  const allItems = objectsStore.getData();
  
  return allItems.filter(item => {
    // Filter by game and type (includes glossary, Library Object, and document)
    if (item.game_id !== gameId) return false;
    if (item.type !== 'glossary' && item.type !== 'Library Object' && item.type !== 'document') return false;
    
    // Handle folder filtering
    if (isInRoot(folderId)) {
      return isInRoot(item.folder);
    } else {
      const allFolderIds = getAllSubfolderIds(gameId, 'library', folderId!);
      return isInFolderSet(item.folder, allFolderIds);
    }
  });
}
